/**
 * Frontend date utilities for GMT+1
 * Works in conjunction with backend dateUtils
 * 
 * NOTE: Uses date-fns for better tree-shaking (~50KB vs moment's ~300KB)
 */

import { 
  format, 
  formatDistance, 
  formatRelative,
  parseISO,
  isValid,
  startOfDay,
  isSameDay,
  differenceInMinutes,
  differenceInHours
} from 'date-fns';

/**
 * Get start of day in GMT+1
 * @param {Date|string} date - Date to normalize
 * @returns {Date} Date set to midnight GMT+1
 */
export const getGMT1StartOfDay = (date = new Date()) => {
  const d = new Date(date);
  return startOfDay(d);
};

/**
 * Compare two dates by date only (ignoring time)
 * @param {Date|string} date1 - First date to compare
 * @param {Date|string} date2 - Second date to compare
 * @returns {boolean} True if same date in GMT+1
 */
export const isSameGMT1Date = (date1, date2) => {
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  return isSameDay(d1, d2);
};

/**
 * Format date for API (YYYY-MM-DD in GMT+1)
 * @param {Date|string} date - Date to format
 * @returns {string} Date string in YYYY-MM-DD format
 */
export const formatDateForAPI = (date = new Date()) => {
  const d = new Date(date);
  return format(d, 'yyyy-MM-dd');
};

// ============================================
// DATE-FNS BASED UTILITIES (to replace moment.js)
// ============================================

/**
 * Format date like moment's "MMMM Do YYYY" -> "January 1st 2025"
 * @param {Date|string} date - Date to format
 * @returns {string} Formatted date string
 */
export const formatDateFull = (date) => {
  if (!date) return '';
  const d = typeof date === 'string' ? parseISO(date) : new Date(date);
  if (!isValid(d)) return '';
  return format(d, 'MMMM do yyyy');
};

/**
 * Format date like moment's "MMMM Do YYYY, h:mm A" -> "January 1st 2025, 3:30 PM"
 * @param {Date|string} date - Date to format
 * @returns {string} Formatted date and time string
 */
export const formatDateTime = (date) => {
  if (!date) return '';
  const d = typeof date === 'string' ? parseISO(date) : new Date(date);
  if (!isValid(d)) return '';
  return format(d, 'MMMM do yyyy, h:mm a');
};

/**
 * Format time like moment's "h:mm A" -> "3:30 PM"
 * @param {Date|string} date - Date to format
 * @returns {string} Formatted time string
 */
export const formatTime = (date) => {
  if (!date) return '';
  const d = typeof date === 'string' ? parseISO(date) : new Date(date);
  if (!isValid(d)) return '';
  return format(d, 'h:mm a');
};

/**
 * Format date like moment's "MMM Do, h:mm A" -> "Jan 1st, 3:30 PM"
 * @param {Date|string} date - Date to format
 * @returns {string} Formatted short date and time string
 */
export const formatShortDateTime = (date) => {
  if (!date) return '';
  const d = typeof date === 'string' ? parseISO(date) : new Date(date);
  if (!isValid(d)) return '';
  return format(d, 'MMM do, h:mm a');
};

/**
 * Get relative time like moment's "fromNow()" -> "2 hours ago"
 * @param {Date|string} date - Date to format
 * @returns {string} Relative time string
 */
export const fromNow = (date) => {
  if (!date) return '';
  const d = typeof date === 'string' ? parseISO(date) : new Date(date);
  if (!isValid(d)) return '';
  return formatDistance(d, new Date(), { addSuffix: true });
};

/**
 * Get duration between two dates in minutes
 * @param {Date|string} start - Start date
 * @param {Date|string} end - End date
 * @returns {number} Duration in minutes
 */
export const getDurationMinutes = (start, end) => {
  if (!start || !end) return 0;
  const startD = typeof start === 'string' ? parseISO(start) : new Date(start);
  const endD = typeof end === 'string' ? parseISO(end) : new Date(end);
  return differenceInMinutes(endD, startD);
};

/**
 * Get duration between two dates in hours
 * @param {Date|string} start - Start date
 * @param {Date|string} end - End date
 * @returns {number} Duration in hours
 */
export const getDurationHours = (start, end) => {
  if (!start || !end) return 0;
  const startD = typeof start === 'string' ? parseISO(start) : new Date(start);
  const endD = typeof end === 'string' ? parseISO(end) : new Date(end);
  return differenceInHours(endD, startD);
};

/**
 * Format duration in human readable format
 * @param {number} minutes - Duration in minutes
 * @returns {string} Formatted duration string like "2h 30m"
 */
export const formatDuration = (minutes) => {
  if (minutes === undefined || minutes === null) return '—';
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours > 0) {
    return `${hours}h ${mins}m`;
  }
  return `${mins}m`;
};

